
import React from 'react';
import Header from '@/components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Droplet, FileText } from 'lucide-react';
import DataExtractionTool from '@/components/DataExtractionTool';

const WaterReports = () => {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 overflow-auto">
      <Header />
      <main className="pt-24 pb-10 w-full mx-0 px-4 md:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="border-gray-200 dark:border-gray-800 shadow-sm hover:shadow-md transition-shadow">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-xl font-bold text-gray-900 dark:text-white">Recent Water Reports</CardTitle>
                  <FileText className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="space-y-4 max-h-[420px] overflow-y-auto">
                    {recentReports.map((report, index) => (
                      <div key={index} className="flex items-center p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                        <div className={`w-2 h-2 rounded-full mr-3 ${reportStatusColors[report.status]}`}></div>
                        <div className="flex-1">
                          <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200">{report.title}</h4>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{report.date}</p>
                        </div>
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400">{report.consumption} m³</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="border-gray-200 dark:border-gray-800 shadow-sm hover:shadow-md transition-shadow">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-xl font-bold text-gray-900 dark:text-white">Water Usage Insights</CardTitle>
                  <Droplet className="h-4 w-4 text-cyan-600" />
                </CardHeader>
                <CardContent>
                  <div className="space-y-4 max-h-[420px] overflow-y-auto">
                    {usageInsights.map((insight, index) => (
                      <div key={index} className="border-b border-gray-200 dark:border-gray-700 pb-4 last:border-0 last:pb-0">
                        <h4 className="text-sm font-medium mb-1 text-gray-800 dark:text-gray-200">{insight.title}</h4>
                        <p className="text-xs text-gray-600 dark:text-gray-400">{insight.description}</p>
                        <div className="mt-2 flex items-center">
                          <div className="flex-1 bg-gray-200 dark:bg-gray-700 h-2 rounded-full overflow-hidden">
                            <div 
                              className="bg-cyan-500 h-full rounded-full" 
                              style={{ width: `${insight.percentage}%` }}
                            ></div>
                          </div>
                          <span className="ml-2 text-xs font-medium text-gray-700 dark:text-gray-300">{insight.percentage}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
          
          <div className="border border-gray-200 dark:border-gray-800 rounded-lg shadow-sm hover:shadow-md transition-shadow bg-white dark:bg-[#0B101C] overflow-hidden">
            <DataExtractionTool className="h-full" />
          </div>
        </div>
      </main>
    </div>
  );
};

// Sample data for the reports page
const recentReports = [
  {
    title: "Monthly Water Usage Report",
    date: "June 1, 2023",
    consumption: 350,
    status: "completed"
  },
  {
    title: "Quarterly Water Analysis",
    date: "April 15, 2023",
    consumption: 980,
    status: "completed"
  },
  {
    title: "Annual Water Conservation Report",
    date: "January 10, 2023",
    consumption: 4120,
    status: "pending"
  },
  {
    title: "Summer Peak Usage Analysis",
    date: "August 5, 2022",
    consumption: 1250,
    status: "processing"
  },
  {
    title: "Water Leakage Detection Report",
    date: "July 22, 2022",
    consumption: 420,
    status: "completed"
  }
];

const reportStatusColors = {
  completed: "bg-green-500",
  pending: "bg-amber-500",
  processing: "bg-blue-500"
};

const usageInsights = [
  {
    title: "Bathroom",
    description: "Bathroom usage accounts for 42% of your total water consumption.",
    percentage: 42
  },
  {
    title: "Kitchen",
    description: "Kitchen water usage represents 28% of your total consumption.",
    percentage: 28
  },
  {
    title: "Laundry",
    description: "Laundry contributes to 18% of your total water usage.",
    percentage: 18
  },
  {
    title: "Outdoor",
    description: "Outdoor water usage (garden, car washing) makes up 12%.",
    percentage: 12
  }
];

export default WaterReports;
